import DOMPurify from 'dompurify';
import './ContentBlocks.css';
import { AthleteMentionBlock } from './AthleteMentionBlock';

/* ───────────────────────────────────────────────────────────────────
   Renders the new modular content-block stack on the story page.

   Channel theme tokens live in ContentBlocks.css and key off
   `data-channel` on the wrapping <section>. The four FLYT-style blocks
   (stat_bar, pull_quote, profile_card, score_bar) auto-adapt — no
   per-channel branching needed in JS.

   Universal embed blocks:
     • html_embed     — UNSANITIZED. Renders whatever the admin pastes,
                        including <style> and <script>. Same posture as
                        Webflow's HTML Embed component.
     • iframe_embed   — Sandboxed by default. Editor can flip the
                        sandbox toggle off for trusted sources.
     • rich_html      — DOMPurify-sanitized (safer middle ground).
   ─────────────────────────────────────────────────────────────────── */

// Tags allowed in rich_html blocks (matches StoryPage.js article_body policy).
const RICH_HTML_ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'p', 'strong', 'em', 'b', 'i', 'u', 'br', 'hr',
  'a', 'img', 'ul', 'ol', 'li', 'blockquote', 'span', 'div',
  'figure', 'figcaption',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
];
const RICH_HTML_ALLOWED_ATTR = ['href', 'src', 'alt', 'class', 'target', 'rel', 'style', 'colspan', 'rowspan'];

const sanitizeRich = (html) =>
  DOMPurify.sanitize(html || '', { ALLOWED_TAGS: RICH_HTML_ALLOWED_TAGS, ALLOWED_ATTR: RICH_HTML_ALLOWED_ATTR });

const HtmlEmbedBlock = ({ data }) => (
  // No sanitization here on purpose — admin is the only author, and
  // they explicitly want Webflow-style raw HTML/CSS/JS support.
  <div
    className="cb-html"
    data-testid="cb-html-embed"
    dangerouslySetInnerHTML={{ __html: data.html || '' }}
  />
);

const IframeEmbedBlock = ({ data }) => {
  const sandbox = data.sandbox === false ? undefined : (data.sandbox_value || 'allow-scripts allow-same-origin allow-popups');
  const height = Number(data.height) > 0 ? Number(data.height) : 600;
  if (!data.url) return null;
  return (
    <div className="cb-iframe-wrap" data-testid="cb-iframe-embed">
      <iframe
        src={data.url}
        title={data.title || 'Embedded content'}
        height={height}
        loading="lazy"
        sandbox={sandbox}
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
};

const RichHtmlBlock = ({ data }) => (
  <div
    className="story-article-body"
    data-testid="cb-rich-html"
    dangerouslySetInnerHTML={{ __html: sanitizeRich(data.html) }}
  />
);

const StatBarBlock = ({ data }) => {
  const stats = Array.isArray(data.stats) ? data.stats : [];
  if (!stats.length) return null;
  return (
    <div className="cb-statbar" data-testid="cb-stat-bar">
      {stats.map((s, i) => (
        // Composite key — items in this small array don't carry stable IDs
        // but they don't reorder within the renderer either; label+value is
        // stable enough to avoid reconciliation bugs.
        <div key={`${s.label || ''}|${s.value || ''}|${i}`} className="cb-statbar__cell">
          <div className="cb-statbar__value">{s.value || '—'}</div>
          <div className="cb-statbar__label">{s.label || ''}</div>
        </div>
      ))}
    </div>
  );
};

const PullQuoteBlock = ({ data }) => {
  if (!data.text) return null;
  return (
    <blockquote className="cb-quote" data-testid="cb-pull-quote">
      <p className="cb-quote__text">“{data.text}”</p>
      {(data.author || data.role) && (
        <div className="cb-quote__attr">
          {data.author && <strong>{data.author}</strong>}
          {data.author && data.role ? ' · ' : ''}
          {data.role || ''}
        </div>
      )}
    </blockquote>
  );
};

const ProfileCardBlock = ({ data }) => {
  if (!data.name && !data.image) return null;
  return (
    <div className="cb-profile" data-testid="cb-profile-card">
      {data.image
        ? <img src={data.image} alt={data.name || ''} className="cb-profile__img" loading="lazy" />
        : <div className="cb-profile__img" aria-hidden="true" />}
      <div>
        <div className="cb-profile__name">{data.name || ''}</div>
        {data.role && <div className="cb-profile__role">{data.role}</div>}
        {data.bio && <div className="cb-profile__bio">{data.bio}</div>}
        {data.instagram && (
          <a
            href={data.instagram.startsWith('http') ? data.instagram : `https://instagram.com/${data.instagram.replace(/^@/, '')}`}
            target="_blank" rel="noopener noreferrer"
            className="cb-profile__ig"
          >
            @{data.instagram.replace(/^@|^https?:\/\/(www\.)?instagram\.com\//, '').replace(/\/$/, '')}
          </a>
        )}
      </div>
    </div>
  );
};

const ScoreBarBlock = ({ data }) => {
  const home = data.home || {};
  const away = data.away || {};
  const periods = Array.isArray(data.periods) ? data.periods : [];
  if (!home.name && !away.name) return null;
  return (
    <div className="cb-score" data-testid="cb-score-bar">
      <div className="cb-score__head">
        <span>{data.label || 'Final'}</span>
        {data.status && <strong>{data.status}</strong>}
      </div>
      <div className="cb-score__row">
        <div className="cb-score__team">
          <div className="cb-score__name">{home.name || '—'}</div>
          <div className="cb-score__points">{home.score ?? '—'}</div>
        </div>
        <div className="cb-score__vs">vs</div>
        <div className="cb-score__team cb-score__team--away">
          <div className="cb-score__name">{away.name || '—'}</div>
          <div className="cb-score__points">{away.score ?? '—'}</div>
        </div>
      </div>
      {periods.length > 0 && (
        <div className="cb-score__periods">
          {periods.map((p, i) => (
            <div key={`${p.label || `P${i + 1}`}|${p.home ?? ''}|${p.away ?? ''}|${i}`} className="cb-score__period">
              <span>{p.label || `P${i + 1}`}</span>
              {p.home ?? '—'} – {p.away ?? '—'}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RENDERERS = {
  html_embed: HtmlEmbedBlock,
  iframe_embed: IframeEmbedBlock,
  rich_html: RichHtmlBlock,
  stat_bar: StatBarBlock,
  pull_quote: PullQuoteBlock,
  profile_card: ProfileCardBlock,
  score_bar: ScoreBarBlock,
  athlete_mention: AthleteMentionBlock,
};

const ContentBlockRenderer = ({ blocks, channel = 'dreams_league' }) => {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  return (
    <section
      className="cb-stack"
      data-channel={channel}
      data-testid="content-block-stack"
    >
      {blocks.map((b) => {
        const Renderer = RENDERERS[b.type];
        if (!Renderer) return null;
        return <Renderer key={b.id || `${b.type}-${b.position || 0}`} data={b.data || {}} />;
      })}
    </section>
  );
};

export default ContentBlockRenderer;
